// frontend/src/components/homepage/PricingSection.js
//
// The pricing section - three plan cards, a literal port of the live-mesh
// prototype's `.pricing` block (P.10 of the homepage port).
//
// The section carries `hl-root` ON ITSELF and imports homepage.css - a leftover
// of the port, when it was shared by the old homepage (which declared none of
// the `--hl-*` tokens) and the live-mesh page. Nested inside the page's own
// `.hl-root` the second declaration repeats the same values and changes
// nothing, and it keeps the section self-sufficient for the screenshot
// harnesses under scripts/ that render it alone.
//
// FILE LOCATION IS LOAD-BEARING. Backend tests parse THIS FILE as text
// (test_solo_price_single_source.py, test_professional_profession_count_single_
// source.py) and it is named individually in the `backend` paths filter of
// .github/workflows/test.yml. Moving or renaming it means updating both.
//
// COPY comes from the /pricing endpoint (app/core/plan_content.py - the single
// source of truth); the literals below are the hand-maintained OFFLINE MIRROR,
// and they are NOT an API-down-only path: PricingContext seeds its state with
// the fallback, so these are what a visitor paints on first load, until
// /pricing resolves. Nothing syncs them automatically - the backend guards above
// fail on drift. Replacing the mirrors with a generated file is
// future_developments.md #167.
//
// THE CARDS ARE NOT <PlanCard>. That component is the CHECKOUT's card
// (components/pricing/PlanCard.js, shared with /payment) and carries that
// surface's own typography and metrics; the prototype's card is a different
// object (28px radius, mono price, gradient hairline on Professional, glass on
// Solo) and the two cannot be reconciled through overrides. The bullets still
// go through renderPlanFeatures - the one `[label](/path)` + `**bold**` parser
// both surfaces share - and the multiplier pills are still keyed by the exact
// bullet string, exactly like plan_content.card_chips. Only the rendering of
// the pill differs: the prototype seats it at the far end of the row, as a flex
// sibling of the text, so it is rendered here rather than inline by the
// renderer.

import React, { Suspense, useState, useCallback, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { usePricing, FREE_TRIAL_DAYS } from "../../contexts/PricingContext";
import { renderPlanFeatures } from "../pricing/planFeatures";
import SectionShell from "../homepage/SectionShell";
import useReveal from "../../motion/useReveal";
import useMotion from "../../motion/useMotion";
import "../../homepage/homepage.css";
import "../../homepage/pricingFaqBlog.css";

// The Enterprise contact form is ~700 lines of MUI dialog that only the
// Enterprise CTA opens. Split out and mounted the first time it is opened; it
// stays mounted from then on so the close transition and a re-open cost
// nothing. Until then the page does not carry it.
const EnterpriseContactModal = React.lazy(() => import("./EnterpriseContactModal"));

// Accent handed to renderPlanFeatures for its anchors. The card CSS restyles
// the anchor to the prototype's plain-ink underline, so this only decides the
// colour of a bullet link in the one place the CSS does not reach (none today).
const SOLO_ACCENT = "#BF5867";
const PRO_ACCENT = "#0D9488";
const ENT_ACCENT = "#E6C988";

/** The prototype's check glyph - one path, stroked by the card's CSS. */
const Check = () => (
  <i aria-hidden="true">
    <svg viewBox="0 0 24 24">
      <path d="M5 12l5 5L20 7" />
    </svg>
  </i>
);

/**
 * The multiplier label for a bullet, or null.
 *
 * OWN properties only, and only string values - the same rule as
 * renderPlanFeatures.chipFor, for the same reason: both the map and the key come
 * off the public /pricing payload, and a bullet worded `constructor` would
 * otherwise resolve to an inherited Object.prototype member.
 */
function chipFor(chips, feature) {
  if (!chips || typeof chips !== "object") return null;
  if (!Object.prototype.hasOwnProperty.call(chips, feature)) return null;
  const label = chips[feature];
  return typeof label === "string" && label ? label : null;
}

/**
 * The prototype's `data-magnet`: the CTA leans toward the pointer while it is
 * over the button and springs back on leave. Mouse only (the prototype skips
 * TOUCH), nothing under motion-off. A direct style write inside the pointer
 * event, as in the prototype - one write per event, no frame loop to own.
 */
function useMagnet(off) {
  const onPointerMove = useCallback(
    (e) => {
      if (off || e.pointerType !== "mouse") return;
      const b = e.currentTarget;
      const r = b.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      b.style.transform = `translate(${dx * 0.22}px,${dy * 0.28}px)`;
    },
    [off]
  );
  const onPointerLeave = useCallback((e) => {
    e.currentTarget.style.transform = "";
  }, []);
  return { onPointerMove, onPointerLeave };
}

/**
 * One plan card. `variant` picks the prototype's `.solo` / `.pro` / `.ent`
 * styling; everything else is content. `features` are the rendered bullet
 * nodes, `chips` the parallel array of pill labels (null where the bullet has
 * none), `cta` the finished anchor or button.
 */
function PlanCardLive({
  variant,
  delay,
  badge,
  name,
  desc,
  price,
  period,
  note,
  box,
  features,
  chips,
  cta,
}) {
  const reveal = useReveal();
  const className = `hl-pr-plan hl-pr-${variant}${reveal.className ? ` ${reveal.className}` : ""}`;
  return (
    <article
      ref={reveal.ref}
      className={className}
      style={delay ? { "--hl-d": delay } : undefined}
    >
      <span className="hl-pr-badge">{badge}</span>
      {/* An <h3>: the plan name is the card's heading under the section's
          <h2>. The class carries every metric, so index.css's h3 rule does
          not reach it. */}
      <h3 className="hl-pr-name">{name}</h3>
      <div className="hl-pr-desc">{desc}</div>
      <div className="hl-pr-price">
        <b>{price}</b>
        {period && <span>{period}</span>}
      </div>
      {/* Rendered even when empty: the prototype reserves the line on every
          card so the boxes below sit on one row. */}
      <div className="hl-pr-note">{note}</div>
      <div className="hl-pr-box">{box}</div>
      <ul>
        {features.map((feature, i) => (
          <li key={i}>
            <Check />
            <span>{feature}</span>
            {chips[i] && (
              <span className="hl-pr-x" aria-hidden="true">
                {chips[i]}
              </span>
            )}
          </li>
        ))}
      </ul>
      {cta}
    </article>
  );
}

const PricingSection = ({ isHebrew }) => {
  const { pricing } = usePricing();
  const { off: motionOff } = useMotion();
  const magnet = useMagnet(motionOff);
  const head = useReveal();
  const foot = useReveal();
  const [enterpriseModalOpen, setEnterpriseModalOpen] = useState(false);
  // Latched on the first open: the lazy chunk is fetched then, never before.
  const [enterpriseModalMounted, setEnterpriseModalMounted] = useState(false);

  // Memoized - only rebuilds when language or pricing changes.
  const text = useMemo(() => {
    const lang = isHebrew ? "he" : "en";
    // Read a bilingual content field for a tier, falling back to the hardcoded
    // value when the API didn't supply content (offline fallback path).
    const pc = (tier, field, fallback) =>
      pricing.tiers?.[tier]?.content?.[field]?.[lang] ?? fallback;

    // `card_plus` and `card_chips` are ONE unit and must be resolved together.
    // Chips are keyed by the EXACT bullet string, and the fallback bullets below
    // are worded slightly differently from plan_content's - so resolving the two
    // through independent `pc()` calls means that during a deploy (new bundle,
    // older backend revision, or any payload that carries bullets but no chip
    // map) the card gets SERVER bullets plus FALLBACK chip keys, which never
    // byte-match, and all three pills silently vanish. Server bullets => server
    // chips, absent or not; fallback bullets => fallback chips.
    const pcPlus = (tier, fallbackFeatures, fallbackChips) => {
      const features = pricing.tiers?.[tier]?.content?.card_plus?.[lang];
      if (!features) return { features: fallbackFeatures, chips: fallbackChips };
      return {
        features,
        chips: pricing.tiers?.[tier]?.content?.card_chips?.[lang] ?? null,
      };
    };

    const professionalPlus = pcPlus(
      "professional",
      isHebrew
        ? [
            "מעקב אחר **4 מקצועות/תחומים** שונים במקביל",
            "עד **60 התראות** במייל על הזדמנויות חמות כל יום",
            "רשת תומכים מורחבת - עד **5 תומכים**",
            "[**אתר עסקי מעוצב**](/business-website) בכתובת אישית משלך - מוכן לפרסום בדקות",
            "**תזכורות follow-up** אוטומטיות לכל פנייה שביצעת",
            "כתובת אימייל נוספת לקבלת התראות",
            "גישה בלעדית לכלים מתקדמים חדשים",
            "עדכונים ופיצ'רים חדשים באופן שוטף",
          ]
        : [
            "Monitoring **4 different professions/fields** simultaneously",
            "Up to **60 daily email alerts** on hot opportunities",
            "Expanded supporter network - up to **5 supporters**",
            "[**A designed business site**](/business-website) at your own URL - ready to publish in minutes",
            "**Automatic follow-up reminders** for every lead you've contacted",
            "An additional email address for receiving alerts",
            "Exclusive access to new advanced tools",
            "Regular updates and new features",
          ],
      // Multiplier pills, keyed by the EXACT bullet string. This map is keyed on
      // the FALLBACK bullets above (worded slightly differently from
      // plan_content's), so the offline card is not chip-less.
      isHebrew
        ? {
            "מעקב אחר **4 מקצועות/תחומים** שונים במקביל": "x4",
            "עד **60 התראות** במייל על הזדמנויות חמות כל יום": "x6",
            "רשת תומכים מורחבת - עד **5 תומכים**": "x2.5",
          }
        : {
            "Monitoring **4 different professions/fields** simultaneously": "x4",
            "Up to **60 daily email alerts** on hot opportunities": "x6",
            "Expanded supporter network - up to **5 supporters**": "x2.5",
          }
    );

    // Derived, never typed: the second copy of the trial length lived here as
    // a literal "14" and nothing could see it drift from
    // tier_config.TRIAL_DURATION_DAYS (which already moved once, 7 -> 14).
    // The constant it reads is the one the backend guard checks.
    const trialTitle = isHebrew
      ? `${FREE_TRIAL_DAYS} ימי ניסיון חינם`
      : `${FREE_TRIAL_DAYS}-Day Free Trial`;
    const trialSubtitle = isHebrew ? "ללא צורך בכרטיס אשראי" : "No credit card required";

    return {
      eyebrow: isHebrew ? "מחירים" : "Pricing",
      title: isHebrew ? "בחר את התוכנית שמתאימה לך" : "Choose Your Plan",

      soloBadge: pc("solo", "badge", isHebrew ? "מצוין להתחלה" : "Great to Start"),
      soloName: "Solo",
      soloDesc: pc(
        "solo",
        "description",
        isHebrew
          ? "לעסקים קטנים ואנשים פרטיים"
          : "For small businesses & individuals"
      ),
      soloPrice: pricing.tiers.solo?.price_display ?? "199",
      soloCta: pc("solo", "cta", isHebrew ? "התחל עכשיו חינם" : "Start Free Now"),
      soloFeatures: pc(
        "solo",
        "card_features",
        isHebrew
          ? [
              "מעקב אחר מקצוע/תחום יחיד",
              "התראות Push באפליקציה ללא הגבלה!",
              "עד 10 התראות במייל על הזדמנויות חמות כל יום",
              "[רשת תומכים](/supporter-network) - הוספה של עד 2 מכרים שימליצו עליכם בתגובות",
              "דשבורד לניהול וצפייה בכל ההזדמנויות העסקיות",
              "ניסוח פניות מותאמות אישית באמצעות AI",
              "התראות מיידיות בזמן אמת",
              "ניתוח עמוק והמלצות לכל ליד",
            ]
          : [
              "Monitoring a single profession/field",
              "Unlimited in-app push notifications!",
              "Up to 10 daily email alerts on hot opportunities",
              "[Supporter network](/supporter-network) - add up to 2 contacts who vouch for you in the comments",
              "Dashboard to manage and view all business opportunities",
              "AI-powered personalized outreach drafting",
              "Real-time instant notifications",
              "Deep lead analysis and recommendations",
            ]
      ),

      professionalBadge: pc(
        "professional",
        "badge",
        // Owner round 3: was "הכי פופולרי" / "Most Popular" - mirrors
        // plan_content.py's professional.badge.
        isHebrew ? "הכי משתלם" : "Best Value"
      ),
      professionalName: "Professional",
      professionalDesc: pc(
        "professional",
        "description",
        isHebrew
          ? "לעסקים בצמיחה עם מעטפת מקצועית רחבה"
          : "For growing businesses with a broad professional suite"
      ),
      professionalPrice: pricing.tiers.professional.price_display,
      professionalPriceNote: pc(
        "professional",
        "card_price_note",
        isHebrew ? "פי 3 יותר משתלם מ-Solo" : "3x better value than Solo"
      ),
      currency: "₪",
      period: isHebrew ? "/חודש" : "/month",
      trialTitle,
      // The prototype's `.pricing-foot`, middle dot included.
      foot: `${trialTitle} · ${trialSubtitle}`,
      professionalCta: pc(
        "professional",
        "cta",
        isHebrew ? "התחל עכשיו חינם" : "Start Free Now"
      ),
      professionalIncludes: pc(
        "professional",
        "includes",
        isHebrew ? "הכל מתוכנית Solo, ובנוסף:" : "Everything in Solo, plus:"
      ),
      professionalFeatures: professionalPlus.features,
      professionalChips: professionalPlus.chips,

      enterpriseBadge: pc(
        "enterprise",
        "badge",
        isHebrew ? "הכי מתקדם" : "Most Advanced"
      ),
      enterpriseName: "Enterprise",
      enterpriseDesc: pc(
        "enterprise",
        "description",
        isHebrew ? "לארגונים וחברות גדולות" : "For organizations & large companies"
      ),
      enterprisePrice: pc(
        "enterprise",
        "price_label",
        isHebrew ? "מותאם אישית" : "Custom"
      ),
      enterpriseNote: isHebrew
        ? "תוכניות גמישות לפי צרכי הארגון"
        : "Flexible plans tailored to your organization's needs",
      enterpriseCta: pc(
        "enterprise",
        "cta",
        isHebrew ? "קבע שיחת היכרות" : "Book a Call"
      ),
      enterpriseIncludes: pc(
        "enterprise",
        "includes",
        isHebrew
          ? "הכל מתוכנית Professional, ובנוסף:"
          : "Everything in Professional, plus:"
      ),
      enterpriseFeatures: pc(
        "enterprise",
        "card_plus",
        isHebrew
          ? [
              "היקף סריקה רחב ביותר ללא הגבלה",
              "ניטור ומעקב אחר אזכורי המותג, המתחרים ואירועים חריגים בתעשייה",
              "זיהוי והתערבות חכמה בשיח רלוונטי לבניית מוניטין חזק",
              "ליווי ותמיכה צמודה בוואטסאפ לאורך כל היום",
              "כיסוי רחב של כל המקצועות והתחומים העסקיים של הארגון",
              "כל סוגי ההתראות ללא הגבלה",
              "הפצת התראות לעד 5 נמענים בארגון",
              "רשת תומכים ארגונית - עד 20 תומכים",
            ]
          : [
              "The broadest scanning coverage, with no limit",
              "Monitoring and tracking of brand mentions, competitors and unusual industry events",
              "Smart detection and intervention in relevant conversations to build a strong reputation",
              "Close, hands-on support on WhatsApp throughout the day",
              "Broad coverage of all your organization's professions and business domains",
              "All alert types with no limits",
              "Alert distribution to up to 5 recipients in the organization",
              "Organization-wide supporter network - up to 20 supporters",
            ]
      ),
    };
  }, [isHebrew, pricing]);

  // Feature bullets may embed `[label](/path)` links and `**bold**`, so each
  // list is parsed into nodes before it reaches the card. Memoized - parsing
  // allocates elements on every call.
  const soloFeatureNodes = useMemo(
    () => renderPlanFeatures(text.soloFeatures, SOLO_ACCENT),
    [text.soloFeatures]
  );
  const professionalFeatureNodes = useMemo(
    () => renderPlanFeatures(text.professionalFeatures, PRO_ACCENT),
    [text.professionalFeatures]
  );
  const enterpriseFeatureNodes = useMemo(
    () => renderPlanFeatures(text.enterpriseFeatures, ENT_ACCENT),
    [text.enterpriseFeatures]
  );
  // The pill labels, parallel to the Professional bullets.
  const professionalChipLabels = useMemo(
    () => text.professionalFeatures.map((f) => chipFor(text.professionalChips, f)),
    [text.professionalFeatures, text.professionalChips]
  );
  const noChips = useMemo(() => text.soloFeatures.map(() => null), [text.soloFeatures]);
  const noEntChips = useMemo(
    () => text.enterpriseFeatures.map(() => null),
    [text.enterpriseFeatures]
  );

  const handleOpenEnterpriseModal = useCallback(() => {
    setEnterpriseModalMounted(true);
    setEnterpriseModalOpen(true);
  }, []);
  const handleCloseEnterpriseModal = useCallback(() => setEnterpriseModalOpen(false), []);

  return (
    <SectionShell
      id="pricing"
      scene="pricing"
      mesh="disperse"
      meshAlpha=".2"
      className="hl-root hl-pr"
    >
      <div
        ref={head.ref}
        className={head.className ? `hl-head hl-head-center ${head.className}` : "hl-head hl-head-center"}
      >
        <div className="hl-eyebrow">{text.eyebrow}</div>
        <h2 className="hl-h2 hl-h2-center">{text.title}</h2>
      </div>

      <div className="hl-pr-plans">
        <PlanCardLive
          variant="solo"
          badge={text.soloBadge}
          name={text.soloName}
          desc={text.soloDesc}
          price={`${text.soloPrice}${text.currency}`}
          period={text.period}
          note=""
          box={text.trialTitle}
          features={soloFeatureNodes}
          chips={noChips}
          cta={
            <RouterLink to="/register" className="hl-pr-btn" {...magnet}>
              {text.soloCta}
            </RouterLink>
          }
        />

        <PlanCardLive
          variant="pro"
          delay=".1s"
          badge={text.professionalBadge}
          name={text.professionalName}
          desc={text.professionalDesc}
          price={`${text.professionalPrice}${text.currency}`}
          period={text.period}
          note={text.professionalPriceNote}
          box={text.professionalIncludes}
          features={professionalFeatureNodes}
          chips={professionalChipLabels}
          cta={
            <RouterLink to="/register" className="hl-pr-btn" {...magnet}>
              {text.professionalCta}
            </RouterLink>
          }
        />

        <PlanCardLive
          variant="ent"
          delay=".2s"
          badge={text.enterpriseBadge}
          name={text.enterpriseName}
          desc={text.enterpriseDesc}
          price={text.enterprisePrice}
          note={text.enterpriseNote}
          box={text.enterpriseIncludes}
          features={enterpriseFeatureNodes}
          chips={noEntChips}
          cta={
            <button
              type="button"
              className="hl-pr-btn"
              onClick={handleOpenEnterpriseModal}
              {...magnet}
            >
              {text.enterpriseCta}
            </button>
          }
        />
      </div>

      <p
        ref={foot.ref}
        className={foot.className ? `hl-pr-foot ${foot.className}` : "hl-pr-foot"}
      >
        {text.foot}
      </p>

      {enterpriseModalMounted && (
        <Suspense fallback={null}>
          <EnterpriseContactModal
            open={enterpriseModalOpen}
            onClose={handleCloseEnterpriseModal}
            isHebrew={isHebrew}
          />
        </Suspense>
      )}
    </SectionShell>
  );
};

export default PricingSection;
